import "mapbox-gl/dist/mapbox-gl.css";
import React, { useState, useRef } from "react";
import MapGL, { Marker, Source, Layer, MapMouseEvent } from "react-map-gl";
import Box from "@material-ui/core/Box";

import { LngLat } from "react-map-gl";

import { defaultSourceLayer } from "./../riskAssessment/defaultSourceLayer";
import { defaultRiskAssessmentMapStyle } from "../map/Constants";
import { useHazardMap } from "./HazardMapContext";
import Pin from "../map/pin";
import BuildingHeightLegend from "../legends/BuildingHeightLegend";
import { saveUserMonitorData } from "../util/userUtils";
import RenderHazardMap from "./../riskAssessment/RenderHazardMap";
import { ScaleBar } from "../map/ScaleBar";

import { lngLatIsInItaly } from "../util/MapUtilsTS";
import { MapStylesMenu } from "../map/MapStylesMenu";
import { MapSearchInput } from "../map/MapSearchInput";
import { MapSearchInputNoCountryCheck } from "../map/MapSearchInputNoCountryCheck";
import { useClientConfig } from "../../context/clientConfigContext";
import type { MapRef } from "react-map-gl";

type RiskAssessmentMapProps = {
  location: LngLat;
  setLocation: Function;
  setPlaceName: Function;
  buildingGeoJsonProp: GeoJsonData;
};

const TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
if (!TOKEN) {
  throw new Error("REACT_APP_MAPBOX_TOKEN is not defined");
}

export const RiskAssessmentMap: React.FC<RiskAssessmentMapProps> = ({
  location,
  setLocation,
  setPlaceName,
  buildingGeoJsonProp,
}) => {
  const { selectedHazard, onHazardChange } = useHazardMap();
  const config = useClientConfig();

  const mapRef = useRef<MapRef>(null);
  const [viewState, setViewState] = useState<PartialViewState>({
    latitude: location.lat,
    longitude: location.lng,
    zoom: 6,
  });

  const [mapStyle, setMapStyle] = useState(defaultRiskAssessmentMapStyle); // when hazard config has mayStyle
  const [sourceLayerData, setSourceLayerData] = useState(defaultSourceLayer); // when hazard config has source layer
  const [sourceLayerDataSecondary, setSourceLayerDataSecondary] = useState(defaultSourceLayer); // when hazard config has 2nd source layer

  const [isBasicMapStyle, setIsBasicMapStyle] = useState(true);
  const [styleLoaded, setStyleLoaded] = useState(false);
  const styleRevRef = useRef(0);

  const onStyleChange = (newStyle: string) => {
    if (mapStyle !== newStyle) {
      // This was a difficult place. For some reason MapGl tries
      // to display the map before style is loaded. The map is displayed without any style.
      // It could be a bug in MapGl or in MapBox or in React.
      // We tried with different gating methods and delays.
      // The workaround that worked well is to force a full remount of MapGL by changing its key.

      // console.log("🔁 Changing map style to", newStyle);
      styleRevRef.current += 1; // force Map remount
      setStyleLoaded(false); // gate Sources until onLoad
      setMapStyle(newStyle);
    } else {
      // console.log("ℹ️ Map style already set to", newStyle);
    }
  };

  const handleGeocoderViewStateChange = (viewState: PartialViewState) => {
    setLocation({ lat: viewState.latitude, lng: viewState.longitude });

    return onMove(viewState);
  };

  // const handleOnResult = (event: GeocodingFeature) => {
  //   const placeName = event.properties.full_address;

  //   if (placeName) {
  //     saveUserMonitorData("riskAssessmentMap", placeName);
  //     setPlaceName(placeName);
  //   }
  // };

  const handleOnResult = (placeName: string) => {
    if (placeName) {
      saveUserMonitorData("riskAssessmentMap", placeName);
      setPlaceName(placeName);
    }
  };

  const onClick = async (event: MapMouseEvent) => {
    if (config.geocoderSearchItalyOnly && !lngLatIsInItaly(event.lngLat)) {
      alert("The selected point is not in Italy, hazard risks are not available");
      // return "";
    }

    setLocation(event.lngLat);

    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${event.lngLat.lng},${event.lngLat.lat}.json?access_token=${TOKEN}`;
    const response = await fetch(url);
    const data = await response.json();
    if (data.features.length === 0) {
      setPlaceName(`Lng: ${event.lngLat.lng} Lat: ${event.lngLat.lat}`);
    } else {
      setPlaceName(`${data.features[0].place_name}`);
    }
  };

  const onMove = (viewState: PartialViewState) => {
    setViewState(viewState);
  };

  const setMapStyleAndClearHazardLayer = (mapStyle: string) => {
    // console.log("Setting map style and clearing hazard layer to", mapStyle);
    onHazardChange(null);
    // setMapStyle(mapStyle);
    onStyleChange(mapStyle);
    setSourceLayerData(defaultSourceLayer);
    setSourceLayerDataSecondary(defaultSourceLayer);
    setIsBasicMapStyle(true);
  };

  /*console.log(
    "RiskAssessmentMap: selectedHazard",
    selectedHazard?.name,
    " loaded:",
    styleLoaded,
    "buildingGeoJsonProp:",
    !!buildingGeoJsonProp,
  );*/

  return (
    <MapGL
      key={`map-${styleRevRef.current}`} // force full remount on style change
      {...viewState}
      ref={mapRef}
      mapboxAccessToken={TOKEN}
      mapStyle={mapStyle}
      onLoad={() => {
        setStyleLoaded(true);
        //console.log("✅ Map loaded; style ready. rev:", styleRevRef.current, "style:", mapStyle);
      }}
      onMove={(evt) => onMove(evt.viewState)}
      onClick={onClick}
    >
      {buildingGeoJsonProp && styleLoaded && (
        <Source id="polygons" type="geojson" data={buildingGeoJsonProp}>
          <Layer
            id="my-geojson-layer"
            type="fill"
            source="polygons"
            paint={{
              "fill-color": "#2296F3",
              "fill-opacity": 0.5,
            }}
          />
        </Source>
      )}

      <RenderHazardMap
        setMapStyle={onStyleChange}
        selectedHazard={selectedHazard}
        sourceLayerData={sourceLayerData}
        setSourceLayerData={setSourceLayerData}
        sourceLayerDataSecondary={sourceLayerDataSecondary}
        setSourceLayerDataSecondary={setSourceLayerDataSecondary}
        styleLoaded={styleLoaded}
        isBasicMapStyle={isBasicMapStyle}
        setBasicMapStyle={setIsBasicMapStyle}
        location={location}
      />

      <BuildingHeightLegend mapStyle={mapStyle} />

      <Marker longitude={location.lng} latitude={location.lat}>
        <Pin size={20} />
      </Marker>

      <Box display="flex">
        <Box width={"50%"}>
          {config.geocoderSearchItalyOnly && (
            <MapSearchInput
              mapRef={mapRef}
              onViewStateChange={handleGeocoderViewStateChange}
              handleOnResult={handleOnResult}
            ></MapSearchInput>
          )}

          {!config.geocoderSearchItalyOnly && (
            <MapSearchInputNoCountryCheck
              mapRef={mapRef}
              onViewStateChange={handleGeocoderViewStateChange}
              handleOnResult={handleOnResult}
            ></MapSearchInputNoCountryCheck>
          )}
        </Box>
        <MapStylesMenu setMapStyle={setMapStyleAndClearHazardLayer}></MapStylesMenu>
      </Box>

      {/* <Geocoder
        mapRef={mapRef}
        onViewportChange={handleGeocoderViewportChange}
        mapboxApiAccessToken={TOKEN}
        countries="it"
        position="top-left"
        onResult={handleOnResult}
        localGeocoder={coordinatesGeocoder}
        reverseGeocode={true}
      /> */}

      <ScaleBar zoom={viewState.zoom} />
    </MapGL>
  );
};
